import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  OnDestroy,
  NgZone
} from '@angular/core';
import { DomSanitizer, SafeUrl, SafeResourceUrl } from '@angular/platform-browser';
import { PatientService } from '../../../core/services/patient.service';
import { ErrorService } from '../../../core/services/error.service';
import { ApiService } from '../../../core/services/api.service';

@Component({
  selector: 'app-patient-document-viewer',
  standalone: false,
  templateUrl: './patient-document-viewer.component.html',
  styleUrls: ['./patient-document-viewer.component.css']
})
export class PatientDocumentViewerComponent implements OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Input() patientAccountNo: number | null = null;
  @Input() documentId: number | null = null;
  /** Optional: view by direct URL (e.g. after upload, before refetch). Fetched as blob and shown in popup to avoid download. */
  @Input() documentUrl: string | null = null;
  /** Optional filename for download (e.g. from document list). */
  @Input() downloadFileName: string | null = null;
  /** Optional: show this message in the viewer when fetch failed (e.g. 404). Keeps popup open so user can read and close. */
  @Input() loadError: string | null = null;
  @Output() close = new EventEmitter<void>();

  imageUrl: SafeUrl | SafeResourceUrl | null = null;
  /** Set from blob.type: 'application/pdf' for PDF, image/* or application/octet-stream for images */
  documentContentType: string | null = null;
  isLoading = false;
  errorMessage: string | null = null;
  /** True when img failed to load (e.g. BMP in browser that does not support it). */
  imageLoadError = false;
  scale = 1;
  rotationDeg = 0;

  private objectUrl: string | null = null;

  constructor(
    private patientService: PatientService,
    private apiService: ApiService,
    private sanitizer: DomSanitizer,
    private errorService: ErrorService,
    private cdr: ChangeDetectorRef,
    private zone: NgZone
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] && !this.isOpen) {
      this.revokeObjectUrl();
      this.resetState();
      return;
    }
    if (!this.isOpen) return;
    if (this.loadError) {
      this.revokeObjectUrl();
      this.resetState();
      this.isLoading = false;
      this.errorMessage = this.loadError;
      this.cdr.detectChanges();
      return;
    }
    const openOrUrl = changes['isOpen'] || changes['documentUrl'];
    const openOrIds = changes['isOpen'] || changes['patientAccountNo'] || changes['documentId'];
    if (this.documentUrl?.trim()) {
      if (openOrUrl) this.loadDocumentFromUrl(this.documentUrl.trim());
    } else if (this.patientAccountNo != null && this.documentId != null) {
      if (openOrIds) this.loadDocument();
    }
  }

  ngOnDestroy(): void {
    this.revokeObjectUrl();
  }

  get imageTransform(): string {
    return `scale(${this.scale}) rotate(${this.rotationDeg}deg)`;
  }

  private loadDocument(): void {
    this.revokeObjectUrl();
    this.resetState();
    const patientAccountNo = Number(this.patientAccountNo);
    const documentId = Number(this.documentId);
    if (isNaN(patientAccountNo) || isNaN(documentId)) {
      this.errorMessage = 'Invalid patient or document.';
      this.cdr.detectChanges();
      return;
    }
    this.isLoading = true;
    this.errorMessage = null;
    this.patientService.getPatientDocument(patientAccountNo, documentId).subscribe({
      next: (blob: Blob) => {
        this.zone.run(() => {
          if (this.isJsonErrorBlob(blob)) {
            this.isLoading = false;
            this.parseJsonErrorBlob(blob);
            this.cdr.detectChanges();
            return;
          }
          this.detectContentTypeAndShow(blob);
        });
      },
      error: (err: { error?: { message?: string }; message?: string }) => {
        this.zone.run(() => {
          this.isLoading = false;
          const msg =
            err?.error?.message ||
            err?.message ||
            (typeof err?.error === 'string' ? err.error : null) ||
            'Failed to load document.';
          this.errorMessage = msg ?? 'Failed to load document.';
          this.errorService.showError(this.errorMessage);
          this.cdr.detectChanges();
        });
      }
    });
  }

  /** Load document from direct URL (e.g. upload response URL). Fetch as blob and show in popup to avoid browser download. */
  private loadDocumentFromUrl(url: string): void {
    this.revokeObjectUrl();
    this.resetState();
    this.isLoading = true;
    this.errorMessage = null;
    const endpoint = this.tryResolveApiEndpoint(url);
    if (endpoint) {
      this.apiService.get<Blob>(endpoint, { responseType: 'blob' }).subscribe({
        next: (blob: Blob) => {
          this.zone.run(() => {
            if (this.isJsonErrorBlob(blob)) {
              this.isLoading = false;
              this.parseJsonErrorBlob(blob);
              this.cdr.detectChanges();
              return;
            }
            this.detectContentTypeAndShow(blob);
          });
        },
        error: (err: any) => {
          this.zone.run(() => {
            this.isLoading = false;
            this.errorMessage = err?.error?.message || err?.message || 'Failed to load document.';
            this.errorService.showError(this.errorMessage ?? 'Failed to load document.');
            this.cdr.detectChanges();
          });
        }
      });
      return;
    }

    fetch(url, { credentials: 'omit', mode: 'cors' })
      .then((res) => {
        if (!res.ok) throw new Error(res.statusText || 'Failed to load document.');
        return res.blob();
      })
      .then((blob: Blob) => {
        this.zone.run(() => {
          if (this.isJsonErrorBlob(blob)) {
            this.isLoading = false;
            this.parseJsonErrorBlob(blob);
            this.cdr.detectChanges();
            return;
          }
          this.detectContentTypeAndShow(blob);
        });
      })
      .catch((err: Error) => {
        this.zone.run(() => {
          this.isLoading = false;
          this.errorMessage = err?.message ?? 'Failed to load document.';
          this.errorService.showError(this.errorMessage);
          this.cdr.detectChanges();
        });
      });
  }

  private tryResolveApiEndpoint(url: string): string | null {
    const full = String(url || '').trim();
    if (!full) return null;
    const base = String(this.apiService.getBaseUrl() || '').replace(/\/+$/, '');
    if (base && full.startsWith(base + '/')) {
      return full.slice(base.length + 1);
    }
    return null;
  }

  /** Sanitize blob URL for binding: use ResourceUrl for iframe (PDF), SafeUrl for img. */
  private sanitizeBlobUrl(url: string, forPdf: boolean): SafeUrl | SafeResourceUrl {
    if (forPdf) {
      return this.sanitizer.bypassSecurityTrustResourceUrl(url);
    }
    return this.sanitizer.bypassSecurityTrustUrl(url);
  }

  private showAsPdf(blob: Blob): void {
    const blobToUse = blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' });
    const newUrl = URL.createObjectURL(blobToUse);
    const safeUrl = this.sanitizeBlobUrl(newUrl, true);

    this.revokeObjectUrl();
    this.objectUrl = newUrl;
    this.imageUrl = safeUrl;
    this.documentContentType = 'application/pdf';
    this.isLoading = false;
    this.errorMessage = null;

    this.cdr.detectChanges();
  }

  private showAsOther(blob: Blob): void {
    this.revokeObjectUrl();
    this.objectUrl = URL.createObjectURL(blob);
    this.imageUrl = this.sanitizeBlobUrl(this.objectUrl, false);
    this.documentContentType = blob.type || null;
    this.isLoading = false;
    this.errorMessage = null;

    this.cdr.detectChanges();
  }

  /** Detect PDF by magic bytes when type is missing so we use iframe instead of img (which can't show PDF). */
  private detectContentTypeAndShow(blob: Blob): void {
    if (blob.size === 0) {
      this.errorMessage = 'Document is empty.';
      this.isLoading = false;
      this.cdr.detectChanges();
      return;
    }
    const explicitType = (blob.type || '').toLowerCase();

    // Fast path: If blob type is already set, use it immediately (no async delay)
    if (explicitType === 'image/bmp' || explicitType === 'image/x-ms-bmp') {
      const bmpBlob = blob.type === 'image/bmp' || blob.type === 'image/x-ms-bmp' ? blob : new Blob([blob], { type: 'image/bmp' });
      this.showAsOther(bmpBlob);
      return;
    }

    if (explicitType === 'application/pdf') {
      this.showAsPdf(blob);
      return;
    }

    if (explicitType === 'application/json') {
      this.isLoading = false;
      this.parseJsonErrorBlob(blob);
      this.cdr.detectChanges();
      return;
    }

    // Fast path: Other image types (jpeg, png, gif, webp, etc.)
    if (explicitType.startsWith('image/')) {
      this.showAsOther(blob);
      return;
    }

    if (explicitType === 'text/plain' || explicitType === 'text/html') {
      if (blob.size < 256) {
        this.errorMessage = 'Document could not be loaded.';
        this.isLoading = false;
        this.cdr.detectChanges();
        return;
      }
      blob.slice(0, 4).arrayBuffer().then((buf) => {
        const arr = new Uint8Array(buf);
        const isPdf = arr.length >= 4 && arr[0] === 0x25 && arr[1] === 0x50 && arr[2] === 0x44 && arr[3] === 0x46;
        if (isPdf) {
          this.showAsPdf(blob);
        } else {
          this.errorMessage = 'Document could not be displayed (unsupported type).';
          this.isLoading = false;
          this.cdr.detectChanges();
        }
      }).catch(() => {
        this.errorMessage = 'Document could not be loaded.';
        this.isLoading = false;
        this.cdr.detectChanges();
      });
      return;
    }

    if (blob.size < 50) {
      this.errorMessage = 'Document could not be loaded.';
      this.isLoading = false;
      this.cdr.detectChanges();
      return;
    }

    // Only do async detection for unknown types (application/octet-stream or empty)
    if (explicitType === '' || explicitType === 'application/octet-stream') {
      // ULTRA-FAST PATH: Show immediately as image (most common case for BMP)
      // Create object URL and display right away, then verify type in background
      this.revokeObjectUrl();
      this.objectUrl = URL.createObjectURL(blob);
      this.imageUrl = this.sanitizeBlobUrl(this.objectUrl, false);
      this.documentContentType = 'image/bmp'; // Assume BMP initially for fastest display
      this.isLoading = false;
      this.errorMessage = null;
      this.cdr.detectChanges();

      // Verify type in background (non-blocking)
      blob.slice(0, 4).arrayBuffer().then((buf) => {
        const arr = new Uint8Array(buf);
        const isPdf = arr.length >= 4 && arr[0] === 0x25 && arr[1] === 0x50 && arr[2] === 0x44 && arr[3] === 0x46;
        const isBmp = arr.length >= 2 && arr[0] === 0x42 && arr[1] === 0x4D; // 'BM' magic
        
        if (isPdf) {
          // It's actually a PDF, switch to PDF viewer
          this.revokeObjectUrl();
          this.showAsPdf(blob);
        } else if (isBmp) {
          // Confirmed BMP - ensure correct type
          if (blob.type !== 'image/bmp') {
            const bmpBlob = new Blob([blob], { type: 'image/bmp' });
            this.revokeObjectUrl();
            this.objectUrl = URL.createObjectURL(bmpBlob);
            this.imageUrl = this.sanitizeBlobUrl(this.objectUrl, false);
            this.documentContentType = 'image/bmp';
            this.cdr.detectChanges();
          }
        }
        // If neither PDF nor BMP, keep showing as image (already displayed)
      }).catch(() => {
        // If detection fails, keep showing as image (already displayed)
      });
      return;
    }
    this.showAsOther(blob);
  }

  /** Only treat as error when body is JSON. Image responses: image/jpeg, image/png, or application/octet-stream (binary image). */
  private isJsonErrorBlob(blob: Blob): boolean {
    const t = (blob.type || '').toLowerCase();
    return t.includes('json');
  }

  private parseJsonErrorBlob(blob: Blob): void {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const text = reader.result as string;
        const j = text ? JSON.parse(text) : null;
        const msg = j?.message ?? j?.error ?? (typeof j?.error === 'string' ? j.error : null) ?? 'Failed to load document.';
        this.errorMessage = msg;
        this.errorService.showError(msg);
      } catch {
        this.errorMessage = 'Failed to load document.';
        this.errorService.showError(this.errorMessage);
      }
      this.cdr.detectChanges();
    };
    reader.readAsText(blob);
  }

  private revokeObjectUrl(): void {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }
    this.imageUrl = null;
  }

  private resetState(): void {
    this.scale = 1;
    this.rotationDeg = 0;
    this.errorMessage = null;
    this.documentContentType = null;
    this.imageLoadError = false;
  }

  onImageError(): void {
    this.imageLoadError = true;
    this.cdr.detectChanges();
  }

  zoomIn(): void {
    this.scale = Math.min(this.scale + 0.25, 4);
    this.cdr.detectChanges();
  }

  zoomOut(): void {
    this.scale = Math.max(this.scale - 0.25, 0.25);
    this.cdr.detectChanges();
  }

  rotate90(): void {
    this.rotationDeg = (this.rotationDeg + 90) % 360;
    this.cdr.detectChanges();
  }

  rotate180(): void {
    this.rotationDeg = (this.rotationDeg + 180) % 360;
    this.cdr.detectChanges();
  }

  rotate270(): void {
    this.rotationDeg = (this.rotationDeg + 270) % 360;
    this.cdr.detectChanges();
  }

  rotate0(): void {
    this.rotationDeg = 0;
    this.cdr.detectChanges();
  }

  rotate360(): void {
    this.rotationDeg = 360;
    this.cdr.detectChanges();
  }

  onPrint(): void {
    if (!this.objectUrl) return;
    const iframe = document.createElement('iframe');
    iframe.style.setProperty('position', 'fixed');
    iframe.style.setProperty('left', '0');
    iframe.style.setProperty('top', '0');
    iframe.style.setProperty('width', '0');
    iframe.style.setProperty('height', '0');
    iframe.style.setProperty('border', 'none');
    iframe.style.setProperty('visibility', 'hidden');
    iframe.src = this.objectUrl;
    document.body.appendChild(iframe);
    const removeIframe = () => {
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
    };
    iframe.onload = () => {
      // Give PDF/image time to render, then open system print dialog (Printer, Copies, Pages, Print/Cancel)
      setTimeout(() => {
        try {
          iframe.contentWindow?.print();
        } finally {
          setTimeout(removeIframe, 1000);
        }
      }, 600);
    };
    iframe.onerror = () => setTimeout(removeIframe, 500);
  }

  onDownload(): void {
    if (!this.objectUrl) return;
    const name = this.downloadFileName?.trim() || this.defaultDownloadFileName();
    const a = document.createElement('a');
    a.href = this.objectUrl;
    a.download = name;
    a.click();
  }

  private defaultDownloadFileName(): string {
    const t = (this.documentContentType || '').toLowerCase();
    if (t.includes('pdf')) return 'document.pdf';
    if (t.includes('jpeg') || t.includes('jpg')) return 'document.jpg';
    if (t.includes('png')) return 'document.png';
    if (t.includes('bmp')) return 'document.bmp';
    return 'document';
  }

  /** Open current document (blob URL) in a new tab as fallback when in-viewer display fails. */
  openInNewTab(): void {
    if (this.objectUrl) {
      window.open(this.objectUrl, '_blank', 'noopener,noreferrer');
    }
  }

  onClose(): void {
    this.close.emit();
  }
}
